// src/lib/askFormat.ts
//
// Cell formatting for the «Preguntar» result tables. The column unit comes
// from the API; a breakdown cell (an object of counts or amounts) is shown as
// «Nombre: valor · Nombre: valor», each key named through the v2 vocabulary.
// Null stays the dash, never 0 (Decision 11).
import type { TranslateFunction } from 'react-admin';
import { DASH, formatCount, formatMoney, formatPct, formatSeconds } from '@/lib/analyticsFormat';
import { termLabel } from '@/lib/v2Vocabulary';
import type { AskColumnUnit } from '@/types/assistant';

const formatPlainNumber = (value: number): string =>
  new Intl.NumberFormat('es-ES', { maximumFractionDigits: 2 }).format(value);

/** A field of a composed value first (`amount`), then a vocabulary word (`no_show`). */
const keyLabel = (key: string, translate: TranslateFunction): string =>
  translate(`resources.insightV2.fields.${key}`, { _: termLabel(key, translate) });

const formatBreakdown = (value: Record<string, unknown>, translate: TranslateFunction): string => {
  const parts = Object.entries(value).map(([key, inner]) => {
    const label = keyLabel(key, translate);
    if (inner === null || inner === undefined) return `${label}: ${DASH}`;
    if (typeof inner === 'number') {
      if (key.endsWith('_pct')) return `${label}: ${formatPct(inner)}`;
      if (key === 'amount' || key.endsWith('_amount')) return `${label}: ${formatMoney(inner)}`;
      return `${label}: ${formatPlainNumber(inner)}`;
    }
    if (typeof inner === 'object') return `${label} (${formatBreakdown(inner as Record<string, unknown>, translate)})`;
    return `${label}: ${termLabel(String(inner), translate)}`;
  });
  return parts.length === 0 ? DASH : parts.join(' · ');
};

export const formatAskCell = (value: unknown, unit: AskColumnUnit, translate: TranslateFunction): string => {
  if (value === null || value === undefined || value === '') return DASH;
  if (typeof value === 'number') {
    switch (unit) {
      case 'count':
        return formatCount(value);
      case 'percent':
        return formatPct(value);
      case 'seconds':
        return formatSeconds(value);
      case 'currency':
        return formatMoney(value);
      default:
        return formatPlainNumber(value);
    }
  }
  if (typeof value === 'object') return formatBreakdown(value as Record<string, unknown>, translate);
  return String(value);
};

/** «2026-09-01» → «01/09/2026». */
export const formatAskDate = (iso: string): string => {
  const [year, month, day] = iso.split('-');
  return year && month && day ? `${day}/${month}/${year}` : iso;
};
